/**
 * Admin → Agent commissions (platform-only).
 *
 * Backed by /v1/admin/resellers/{id}/agents and /v1/admin/agents/{id}.
 * Gated by RequirePlatform on the backend; the UI hides the section
 * for non-platform admins so this is just defense-in-depth.
 *
 * Agents are sales-rep / channel-partner identities that get a
 * percentage of a reseller's gross or net margin. They never debit
 * any tenant balance — the commission is a derived report.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type AgentBasis = 'gross_revenue' | 'net_margin';

export interface AgentCommission {
  id: number;
  user_id: number;
  user_email: string;
  user_full_name?: string;
  reseller_id: number;
  reseller_name?: string;
  percentage: number;
  basis: AgentBasis;
  is_active: boolean;
  effective_from: string;
  effective_to?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface AgentsResponse {
  items: AgentCommission[];
  total: number;
}

export interface CreateAgentInput {
  resellerID: number;
  email: string;
  full_name: string;
  percentage: number;
  basis: AgentBasis;
  effective_from?: string;
  effective_to?: string;
  notes?: string;
}

export interface UpdateAgentInput {
  id: number;
  percentage: number;
  basis: AgentBasis;
  is_active: boolean;
  effective_from?: string;
  effective_to?: string;
  notes?: string;
}

const AGENTS_KEY = (resellerID: number) =>
  ['admin', 'resellers', resellerID, 'agents'] as const;

export function useAgents(resellerID: number, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: AGENTS_KEY(resellerID),
    queryFn: () =>
      api.get<AgentsResponse>(`/v1/admin/resellers/${resellerID}/agents`),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ resellerID, ...body }: CreateAgentInput) =>
      api.post<AgentCommission>(`/v1/admin/resellers/${resellerID}/agents`, body),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: AGENTS_KEY(vars.resellerID) });
    },
  });
}

export function useUpdateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateAgentInput) =>
      api.patch<AgentCommission>(`/v1/admin/agents/${id}`, body),
    onSuccess: (data) => {
      // Bust the agents-by-reseller cache for whichever reseller the
      // updated row points at.
      void qc.invalidateQueries({ queryKey: AGENTS_KEY(data.reseller_id) });
    },
  });
}

/**
 * Soft-disable an agent. The row stays for audit; only future
 * commissions stop accruing. The backend returns 204 No Content.
 */
export function useDisableAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; resellerID: number }) =>
      api.delete<void>(`/v1/admin/agents/${id}`),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: AGENTS_KEY(vars.resellerID) });
    },
  });
}
